import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Phone, QrCode, ShieldCheck, MapPin, HeartPulse, Users } from "lucide-react";

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-slate-200">
        <div className="container-app flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_bracelet-alert/artifacts/suao9dvz_SFA%20Logo%20-%20long.png" 
              alt="Stress-Free Aging" 
              className="h-10 w-auto object-contain"
            />
            <span className="text-2xl font-bold text-teal-600" style={{ fontFamily: 'Manrope, sans-serif' }}>
              SOS
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost" className="text-slate-600 hover:text-teal-600" data-testid="nav-login-btn">
                Login
              </Button>
            </Link>
            <a href="https://stressfreeaging.com" target="_blank" rel="noopener noreferrer">
              <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-6" data-testid="nav-main-site-btn">
                Main Site
              </Button>
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 hero-gradient">
        <div className="container-app">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-teal-600 leading-tight mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Stress-Free Aging SOS
              </h1>
              <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <ShieldCheck className="h-4 w-4" />
                HIPAA-Conscious Emergency ID
              </div>
              <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 leading-tight mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Peace of Mind for <span className="text-teal-600">Families</span> & <span className="text-teal-600">Caregivers</span>
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed max-w-xl">
                Our QR-enabled bracelets connect your loved ones to emergency help instantly. 
                One scan provides critical information to first responders when every second counts.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/order">
                  <Button className="h-14 px-8 bg-teal-600 hover:bg-teal-700 text-white text-lg font-semibold rounded-full shadow-lg shadow-teal-600/20" data-testid="hero-get-bracelet-btn">
                    Order Bracelet Here
                  </Button>
                </Link>
                <Link to="/login">
                  <Button variant="outline" className="h-14 px-8 border-slate-300 text-slate-700 hover:bg-slate-50 text-lg font-medium rounded-full" data-testid="hero-admin-login-btn">
                    Admin Login
                  </Button>
                </Link>
              </div>
            </div>
            <div className="animate-slide-up stagger-2">
              <img
                src="https://customer-assets.emergentagent.com/job_b27e985d-d66e-4f89-aa44-c3370ef952c0/artifacts/q5dx831y_imagejpeg_1%284%29.jpg"
                alt="Senior wearing QR code bracelet for emergency identification"
                className="rounded-2xl shadow-2xl w-full object-cover h-[400px] lg:h-[500px]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-slate-50">
        <div className="container-app">
          <div className="text-center mb-16 animate-fade-in">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              How It Works
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Simple, secure, and life-saving technology for seniors and their families
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-2xl border border-slate-100 card-hover animate-slide-up stagger-1">
              <div className="w-14 h-14 bg-teal-50 rounded-xl flex items-center justify-center mb-6">
                <QrCode className="h-7 w-7 text-teal-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                1. Get Your Bracelet
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Each bracelet has a unique QR code. Order from our website and register your loved one's information.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-2xl border border-slate-100 card-hover animate-slide-up stagger-2">
              <div className="w-14 h-14 bg-teal-50 rounded-xl flex items-center justify-center mb-6">
                <Users className="h-7 w-7 text-teal-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                2. Register Profile
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Scan the QR code to add essential info: photo, emergency contact, allergies, and care needs.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-2xl border border-slate-100 card-hover animate-slide-up stagger-3">
              <div className="w-14 h-14 bg-red-50 rounded-xl flex items-center justify-center mb-6">
                <Phone className="h-7 w-7 text-red-500" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                3. Instant Emergency Access
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Anyone can scan the bracelet to see vital info and call emergency contacts with one tap.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Emergency Responder Section */}
      <section className="py-20 bg-white">
        <div className="container-app">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1 animate-slide-up">
              <img
                src="https://images.pexels.com/photos/8942262/pexels-photo-8942262.jpeg"
                alt="Paramedic looking at device"
                className="rounded-2xl shadow-xl w-full object-cover h-[400px]"
              />
            </div>
            <div className="order-1 lg:order-2 animate-fade-in">
              <div className="inline-flex items-center gap-2 bg-red-50 text-red-600 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <HeartPulse className="h-4 w-4" />
                For First Responders
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Critical Information When It Matters Most
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                When seconds count, our QR bracelets give EMS and emergency responders instant access to:
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <ShieldCheck className="h-4 w-4 text-teal-600" />
                  </div>
                  <span className="text-slate-700">Photo identification of the individual</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <ShieldCheck className="h-4 w-4 text-teal-600" />
                  </div>
                  <span className="text-slate-700">Emergency contact with one-tap calling</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <ShieldCheck className="h-4 w-4 text-teal-600" />
                  </div>
                  <span className="text-slate-700">Allergy information and dementia status</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <MapPin className="h-4 w-4 text-teal-600" />
                  </div>
                  <span className="text-slate-700">GPS location for faster response</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-teal-600">
        <div className="container-app text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Protect Your Loved Ones Today
          </h2>
          <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto">
            Join thousands of families who trust Stress-Free Aging SOS for peace of mind.
          </p>
          <Link to="/order">
            <Button className="h-14 px-10 bg-white text-teal-600 hover:bg-teal-50 text-lg font-semibold rounded-full shadow-xl" data-testid="cta-order-btn">
              Place Order Now
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-slate-900 text-slate-400">
        <div className="container-app">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_bracelet-alert/artifacts/suao9dvz_SFA%20Logo%20-%20long.png" 
                alt="Stress-Free Aging" 
                className="h-10 w-auto object-contain brightness-0 invert"
              />
            </div>
            <div className="flex items-center gap-6 text-sm">
              <a href="https://stressfreeaging.com" target="_blank" rel="noopener noreferrer" className="hover:text-teal-400 transition-colors">
                Main Website
              </a>
              <a href="https://stressfreeaging.com/contact" target="_blank" rel="noopener noreferrer" className="hover:text-teal-400 transition-colors">
                Contact
              </a>
              <a href="https://stressfreeaging.com/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-teal-400 transition-colors">
                Privacy Policy
              </a>
            </div>
            <p className="text-sm">
              © {new Date().getFullYear()} Stress-Free Aging. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
